import type { LucideIcon } from 'lucide-react';
import {
    Grid2X2,
    Plus,
    RotateCcw,
    SlidersHorizontal,
    Table2,
} from 'lucide-react';
import {
    useEffect,
    useMemo,
    useState,
    type FormEvent,
    type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import NexusLayout from '../../Layouts/NexusLayout';
import api from '../../services/api';
import { FilterBar, FilterField, FilterSubmitButton } from '../data/FilterBar';
import { PageHeader } from '../data/PageHeader';
import { Pagination, type PaginatorLink } from '../data/Pagination';
import { DataTableColumnsMenu } from '../data/DataTableColumnsMenu';
import { SearchField } from '../forms/SearchField';
import { Input } from '../ui/Input';
import { CountBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardHead } from '../ui/Card';
import { TableActions } from '../ui/TableActions';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Select } from '../ui/Select';
import { SngpcEntityOffcanvas, type SngpcFormField } from './SngpcEntityOffcanvas';

type RowRecord = Record<string, unknown> & { id?: number | string };

export type SngpcColumn<TRow extends RowRecord> = {
    id: string;
    label: string;
    render: (row: TRow) => ReactNode;
    primary?: boolean;
    align?: 'left' | 'center' | 'right';
    hideOnMobile?: boolean;
};

export type SngpcFilterField = {
    name: string;
    label: string;
    type?: 'select' | 'date' | 'text';
    placeholder?: string;
    options?: Array<{ value: string; label: string }>;
};

type PaginatedResponse<TRow> = {
    data: TRow[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
    links: PaginatorLink[];
};

type ViewMode = 'table' | 'grid';

type SngpcListingPageProps<TRow extends RowRecord> = {
    title: string;
    pageTitle?: string;
    description: string;
    icon: LucideIcon;
    endpoint: string;
    columns: SngpcColumn<TRow>[];
    emptyMessage: string;
    searchLabel?: string;
    searchPlaceholder?: string;
    actionLabel?: string;
    onAction?: () => void;
    filters?: ReactNode;
    filterFields?: SngpcFilterField[];
    editFields?: SngpcFormField[];
    editTitle?: string;
    createPath?: string;
    editPath?: (row: TRow) => string;
    canEdit?: boolean | ((row: TRow) => boolean);
    canDelete?: boolean | ((row: TRow) => boolean);
    deleteName?: (row: TRow) => string;
    initialPerPage?: number;
};

function emptyPage<TRow>(): PaginatedResponse<TRow> {
    return {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: null,
        to: null,
        links: [],
    };
}

function normalizeResponse<TRow>(payload: unknown): PaginatedResponse<TRow> {
    if (Array.isArray(payload)) {
        return {
            data: payload as TRow[],
            current_page: 1,
            last_page: 1,
            per_page: payload.length || 20,
            total: payload.length,
            from: payload.length ? 1 : null,
            to: payload.length || null,
            links: [],
        };
    }

    const value = (payload ?? {}) as Partial<PaginatedResponse<TRow>>;
    const data = Array.isArray(value.data) ? value.data : [];

    return {
        data,
        current_page: value.current_page ?? 1,
        last_page: value.last_page ?? 1,
        per_page: value.per_page ?? 20,
        total: value.total ?? data.length,
        from: value.from ?? (data.length ? 1 : null),
        to: value.to ?? (data.length || null),
        links: Array.isArray(value.links) ? value.links : [],
    };
}

function pageFromUrl(url: string | null): number | null {
    if (!url) return null;

    try {
        const parsed = new URL(url, window.location.origin);
        const page = Number(parsed.searchParams.get('page'));
        return Number.isFinite(page) && page > 0 ? page : null;
    } catch {
        return null;
    }
}

export default function SngpcListingPage<TRow extends RowRecord>({
    title,
    pageTitle = title,
    description,
    icon: Icon,
    endpoint,
    columns,
    emptyMessage,
    searchLabel = 'Buscar por nome ou código',
    searchPlaceholder = 'Buscar...',
    actionLabel,
    onAction,
    filters,
    filterFields = [],
    editFields = [],
    editTitle = title,
    createPath,
    editPath,
    canEdit = false,
    canDelete = false,
    deleteName,
    initialPerPage = 20,
}: SngpcListingPageProps<TRow>) {
    const navigate = useNavigate();
    const [pageData, setPageData] = useState<PaginatedResponse<TRow>>(emptyPage);
    const [draftSearch, setDraftSearch] = useState('');
    const [search, setSearch] = useState('');
    const [draftFilters, setDraftFilters] = useState<Record<string, string>>({});
    const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(initialPerPage);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [editingRow, setEditingRow] = useState<TRow | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [compactActions, setCompactActions] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
        () => Object.fromEntries(columns.map((column) => [column.id, true])),
    );
    const [columnOrder, setColumnOrder] = useState<string[]>(() => columns.map((column) => column.id));

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        api.get(endpoint, {
            params: {
                page,
                per_page: perPage,
                ...(search ? { search } : {}),
                ...Object.fromEntries(
                    Object.entries(appliedFilters).filter(([, value]) => value !== ''),
                ),
            },
            signal: controller.signal,
        })
            .then((response) => setPageData(normalizeResponse<TRow>(response.data)))
            .catch((requestError: unknown) => {
                if (controller.signal.aborted) return;
                console.error(`Erro ao carregar ${endpoint}:`, requestError);
                setError('Não foi possível carregar os registros. Tente novamente.');
                setPageData(emptyPage());
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [appliedFilters, endpoint, page, perPage, reloadToken, search]);

    const activeColumns = useMemo(
        () => columnOrder
            .map((id) => columns.find((column) => column.id === id))
            .filter((column): column is SngpcColumn<TRow> => Boolean(column && visibleColumns[column.id])),
        [columnOrder, columns, visibleColumns],
    );

    function submitFilters(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPage(1);
        setSearch(draftSearch.trim());
        setAppliedFilters(draftFilters);
    }

    function clearFilters() {
        setDraftSearch('');
        setSearch('');
        setDraftFilters({});
        setAppliedFilters({});
        setPage(1);
    }

    function rowCanEdit(row: TRow) {
        return typeof canEdit === 'function' ? canEdit(row) : canEdit;
    }

    function rowCanDelete(row: TRow) {
        return typeof canDelete === 'function' ? canDelete(row) : canDelete;
    }

    async function saveRow(values: Record<string, unknown>) {
        if (editingRow?.id) {
            await api.put(`${endpoint}/${editingRow.id}`, values);
        } else {
            await api.post(endpoint, values);
        }
        setMessage(editingRow ? 'Registro atualizado com sucesso.' : 'Registro incluído com sucesso.');
        setReloadToken((value) => value + 1);
    }

    function openCreate() {
        if (onAction) return onAction();
        if (createPath) return navigate(createPath);
        setEditingRow(null);
        setFormOpen(true);
    }

    function openEdit(row: TRow) {
        if (editPath) return navigate(editPath(row));
        setEditingRow(row);
        setFormOpen(true);
    }

    async function deleteRow(row: TRow) {
        if (!row.id) return;
        setError(null);
        setMessage(null);
        try {
            await api.delete(`${endpoint}/${row.id}`);
            setMessage('Registro excluído com sucesso.');
            if (pageData.data.length === 1 && page > 1) {
                setPage((current) => current - 1);
            } else {
                setReloadToken((value) => value + 1);
            }
        } catch (requestError) {
            const response = requestError as { response?: { data?: { message?: string } } };
            setError(response.response?.data?.message ?? 'Não foi possível excluir o registro.');
            throw requestError;
        }
    }

    const hasRowActions = editFields.length > 0 || Boolean(editPath) || canDelete !== false;

    function handlePageNavigate(url: string | null) {
        const nextPage = pageFromUrl(url);
        if (nextPage) setPage(nextPage);
    }

    return (
        <NexusLayout title={pageTitle}>
            <div className="nx-page">
                <PageHeader
                    icon={<Icon size={21} strokeWidth={2} />}
                    title={title}
                    description={description}
                    actions={
                        actionLabel ? (
                            <Button type="button" onClick={openCreate}>
                                <Plus size={16} strokeWidth={2.2} />
                                {actionLabel}
                            </Button>
                        ) : null
                    }
                />

                <Card className="nx-page-card nx-sngpc-listing-card">
                    <CardHead
                        title="Listagem"
                        actions={
                            <div className="nx-card-head-actions nx-listing-toolbar">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="ghost">
                                            <Grid2X2 size={16} />
                                            Visualização
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuLabel>Tipo de visualização</DropdownMenuLabel>
                                        <DropdownMenuRadioGroup
                                            value={viewMode}
                                            onValueChange={(value) => setViewMode(value as ViewMode)}
                                        >
                                            <DropdownMenuRadioItem value="table">
                                                <Table2 size={15} /> Tabela
                                            </DropdownMenuRadioItem>
                                            <DropdownMenuRadioItem value="grid">
                                                <Grid2X2 size={15} /> Grade
                                            </DropdownMenuRadioItem>
                                        </DropdownMenuRadioGroup>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <DataTableColumnsMenu
                                    options={columns}
                                    columnOrder={columnOrder}
                                    visibility={visibleColumns}
                                    onToggle={(id, visible) =>
                                        setVisibleColumns((current) => ({ ...current, [id]: visible }))
                                    }
                                    onReorder={(order) => setColumnOrder([...order])}
                                    onReset={() => {
                                        setVisibleColumns(Object.fromEntries(columns.map((column) => [column.id, true])));
                                        setColumnOrder(columns.map((column) => column.id));
                                    }}
                                />

                                <Button
                                    type="button"
                                    variant="ghost"
                                    aria-pressed={compactActions}
                                    onClick={() => setCompactActions((value) => !value)}
                                >
                                    <SlidersHorizontal size={15} />
                                    Modo de Ações
                                </Button>

                                <CountBadge count={`${pageData.total} registros`} />
                            </div>
                        }
                    />

                    <FilterBar
                        onSubmit={submitFilters}
                        search={
                            <SearchField
                                label={searchLabel}
                                placeholder={searchPlaceholder}
                                value={draftSearch}
                                onChange={(event) => setDraftSearch(event.target.value)}
                            />
                        }
                        filters={
                            <>
                                {filters}
                                {filterFields.map((field) => (
                                    <FilterField
                                        key={field.name}
                                        label={field.label}
                                        className="nx-sngpc-filter-field"
                                    >
                                        {field.type === 'date' || field.type === 'text' ? (
                                            <Input
                                                type={field.type === 'date' ? 'date' : 'text'}
                                                placeholder={field.placeholder}
                                                value={draftFilters[field.name] ?? ''}
                                                onChange={(event) =>
                                                    setDraftFilters((current) => ({
                                                        ...current,
                                                        [field.name]: event.target.value,
                                                    }))
                                                }
                                            />
                                        ) : (
                                            <Select
                                                filter
                                                value={draftFilters[field.name] ?? ''}
                                                onChange={(event) =>
                                                    setDraftFilters((current) => ({
                                                        ...current,
                                                        [field.name]: event.target.value,
                                                    }))
                                                }
                                            >
                                                <option value="">Todos</option>
                                                {field.options?.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    </FilterField>
                                ))}
                                <FilterField label="Por página">
                                    <Select
                                        filter
                                        value={perPage}
                                        onChange={(event) => {
                                            setPage(1);
                                            setPerPage(Number(event.target.value));
                                        }}
                                    >
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                    </Select>
                                </FilterField>
                            </>
                        }
                        actions={
                            <>
                                <Button type="button" variant="ghost" onClick={clearFilters}>
                                    <RotateCcw size={15} aria-hidden />
                                    Limpar
                                </Button>
                                <FilterSubmitButton />
                            </>
                        }
                    />

                    {error ? <div className="nx-sngpc-error" role="alert">{error}</div> : null}
                    {message ? <div className="nx-sngpc-message is-success" role="status">{message}</div> : null}

                    {viewMode === 'table' ? (
                        <div className="nx-table-wrap">
                            <table
                                className={`nx-table nx-sngpc-table ${
                                    compactActions ? 'nx-table--compact' : ''
                                }`}
                            >
                                <thead>
                                    <tr>
                                        {activeColumns.map((column) => (
                                            <th
                                                key={column.id}
                                                className={`nx-cell-${column.align ?? 'left'} ${
                                                    column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''
                                                }`}
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                        {hasRowActions ? <th className="nx-table-actions">Ações</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={Math.max(activeColumns.length + (hasRowActions ? 1 : 0), 1)} className="nx-table-loading">
                                                Carregando registros...
                                            </td>
                                        </tr>
                                    ) : pageData.data.length === 0 ? (
                                        <tr>
                                            <td colSpan={Math.max(activeColumns.length + (hasRowActions ? 1 : 0), 1)} className="nx-sngpc-empty-cell">
                                                {emptyMessage}
                                            </td>
                                        </tr>
                                    ) : (
                                        pageData.data.map((row, rowIndex) => (
                                            <tr key={row.id ?? rowIndex}>
                                                {activeColumns.map((column) => (
                                                    <td
                                                        key={column.id}
                                                        className={`nx-cell-${column.align ?? 'left'} ${
                                                            column.primary ? 'nx-table-name' : ''
                                                        } ${column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''}`}
                                                    >
                                                        {column.render(row)}
                                                    </td>
                                                ))}
                                                {hasRowActions ? (
                                                    <td className="nx-table-actions">
                                                        <TableActions
                                                            mode={compactActions ? 'dropdown' : 'buttons'}
                                                            onEdit={
                                                                rowCanEdit(row) && (editFields.length > 0 || editPath)
                                                                    ? () => openEdit(row)
                                                                    : undefined
                                                            }
                                                            onDelete={
                                                                rowCanDelete(row)
                                                                    ? () => deleteRow(row)
                                                                    : undefined
                                                            }
                                                            deleteTitle="Excluir registro"
                                                            deleteDescription={`Tem certeza de que deseja excluir ${
                                                                deleteName?.(row) ?? 'este registro'
                                                            }? Esta ação não poderá ser desfeita.`}
                                                        />
                                                    </td>
                                                ) : null}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : loading ? (
                        <div className="nx-sngpc-grid-loading">Carregando registros...</div>
                    ) : pageData.data.length === 0 ? (
                        <div className="nx-sngpc-grid-empty">{emptyMessage}</div>
                    ) : (
                        <div className="nx-sngpc-record-grid">
                            {pageData.data.map((row, rowIndex) => (
                                <article className="nx-sngpc-record" key={row.id ?? rowIndex}>
                                    {activeColumns.map((column) => (
                                        <div key={column.id} className="nx-sngpc-record-field">
                                            <span>{column.label}</span>
                                            <strong>{column.render(row)}</strong>
                                        </div>
                                    ))}
                                    {hasRowActions ? (
                                        <div className="nx-sngpc-record-actions">
                                            <TableActions
                                                variant="labeled"
                                                mode={compactActions ? 'dropdown' : 'buttons'}
                                                onEdit={
                                                    rowCanEdit(row) && (editFields.length > 0 || editPath)
                                                        ? () => openEdit(row)
                                                        : undefined
                                                }
                                                onDelete={
                                                    rowCanDelete(row) ? () => deleteRow(row) : undefined
                                                }
                                                deleteDescription={`Tem certeza de que deseja excluir ${
                                                    deleteName?.(row) ?? 'este registro'
                                                }? Esta ação não poderá ser desfeita.`}
                                            />
                                        </div>
                                    ) : null}
                                </article>
                            ))}
                        </div>
                    )}

                    <Pagination
                        from={pageData.from}
                        to={pageData.to}
                        total={pageData.total}
                        lastPage={pageData.last_page}
                        links={pageData.links}
                        onNavigate={handlePageNavigate}
                    />
                </Card>

                <SngpcEntityOffcanvas
                    open={formOpen}
                    row={editingRow}
                    entityName={editTitle}
                    fields={editFields}
                    onOpenChange={(open) => {
                        setFormOpen(open);
                        if (!open) setEditingRow(null);
                    }}
                    onSave={saveRow}
                />
            </div>
        </NexusLayout>
    );
}
